import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, CheckCircle2, Lock, Tag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APP_ID;
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;

const inputCls =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7B1E2B] focus:border-transparent';

const loadSquareSdk = () =>
  new Promise((resolve, reject) => {
    if (window.Square) return resolve(window.Square);
    const existing = document.querySelector('script[src*="web.squarecdn.com"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Square));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://web.squarecdn.com/v1/square.js';
    script.onload = () => resolve(window.Square);
    script.onerror = reject;
    document.head.appendChild(script);
  });

const CheckoutForm = ({ onClose }) => {
  const { cart, getCartTotal, clearCart } = useCart();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', address: '', city: '', state: '', zip: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState('');
  const [cardReady, setCardReady] = useState(false);
  const cardRef = useRef(null);
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  const subtotal = getCartTotal();
  const discount = promo ? promo.discount_amount : 0;
  const total = Math.max(subtotal - discount, 0) + 9.99;

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/discount-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoInput.trim(), subtotal }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invalid promo code');
      setPromo(data);
      toast.success(`Code ${data.code} applied — you saved $${data.discount_amount.toFixed(2)}!`);
    } catch (err) {
      setPromo(null);
      toast.error(err.message);
    } finally {
      setPromoLoading(false);
    }
  };

  useEffect(() => {
    let card;
    let cancelled = false;
    (async () => {
      try {
        const Square = await loadSquareSdk();
        const payments = Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
        card = await payments.card();
        if (cancelled) { card.destroy(); return; }
        await card.attach('#square-card-container');
        cardRef.current = card;
        setCardReady(true);
      } catch (err) {
        console.error('Square init failed:', err);
        toast.error('Could not load the secure payment form. Please refresh and try again.');
      }
    })();
    return () => {
      cancelled = true;
      if (cardRef.current) { cardRef.current.destroy(); cardRef.current = null; }
    };
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.address.trim() || !form.city.trim()) {
      toast.error('Please fill in your name, email, and shipping address');
      return;
    }
    if (!cardRef.current) {
      toast.error('Payment form is still loading. Please wait a moment.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await cardRef.current.tokenize();
      if (result.status !== 'OK') {
        const msg = result.errors?.[0]?.message || 'Please check your card details.';
        toast.error(msg);
        setSubmitting(false);
        return;
      }
      const res = await fetch(`${API_URL}/api/payments/charge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_id: result.token,
          discount_code: promo ? promo.code : '',
          ...form,
          items: cart.map((i) => ({
            product_id: i.id, name: i.name, price: i.price, quantity: i.quantity, image: i.image,
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Payment failed');
      setReceiptUrl(data.receipt_url || '');
      setOrderPlaced(true);
      clearCart();
    } catch (err) {
      toast.error(err.message || 'Something went wrong processing your payment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {orderPlaced ? (
          <div className="p-10 text-center" data-testid="order-success">
            <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-2xl font-serif font-bold text-[#001B2A] mb-3">Payment Successful!</h3>
            <p className="text-gray-600 mb-6">
              Thank you, {form.name}! Your payment has been processed and your order is confirmed.
              Your items will ship soon.
            </p>
            {receiptUrl && (
              <a
                data-testid="order-receipt-link"
                href={receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block mb-4 text-sm text-[#7B1E2B] underline"
              >
                View your Square receipt
              </a>
            )}
            <button
              data-testid="order-success-close"
              onClick={onClose}
              className="px-8 py-3 bg-[#001B2A] text-white rounded-md font-semibold hover:bg-[#7B1E2B] transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h3 className="text-xl font-serif font-bold text-[#001B2A]">Checkout</h3>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" data-testid="checkout-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div className="bg-gray-50 rounded-md p-3 text-sm">
                {cart.map((i) => (
                  <div key={i.id} className="flex justify-between py-1">
                    <span>{i.name} × {i.quantity}</span>
                    <span className="font-semibold">${(i.price * i.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-gray-200 mt-2 pt-2">
                  <span>Subtotal</span>
                  <span data-testid="checkout-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                {promo && (
                  <div className="flex justify-between py-1 text-green-700">
                    <span className="flex items-center gap-1">
                      <Tag className="h-3.5 w-3.5" /> {promo.code}
                      <button type="button" onClick={() => { setPromo(null); setPromoInput(''); }} className="ml-1 text-xs text-gray-400 underline" data-testid="remove-promo">remove</button>
                    </span>
                    <span data-testid="checkout-discount">-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between py-1">
                  <span>Shipping (flat rate)</span>
                  <span data-testid="checkout-shipping">$9.99</span>
                </div>
                <div className="flex justify-between border-t border-gray-200 mt-1 pt-2 font-bold text-[#001B2A]">
                  <span>Total</span>
                  <span data-testid="checkout-total">${total.toFixed(2)}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <input
                  data-testid="promo-code-input"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  placeholder="Promo code"
                  className={`${inputCls} uppercase flex-1`}
                  disabled={!!promo}
                />
                <button
                  type="button"
                  data-testid="apply-promo-button"
                  onClick={applyPromo}
                  disabled={promoLoading || !!promo}
                  className="px-5 py-2 bg-[#001B2A] text-white rounded-md text-sm font-semibold hover:bg-[#7B1E2B] transition-colors disabled:opacity-50"
                >
                  {promoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : promo ? 'Applied ✓' : 'Apply'}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <input data-testid="checkout-name" name="name" value={form.name} onChange={handleChange} placeholder="Full Name *" className={inputCls} />
                <input data-testid="checkout-email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="Email *" className={inputCls} />
              </div>
              <input data-testid="checkout-phone" name="phone" value={form.phone} onChange={handleChange} placeholder="Phone" className={inputCls} />
              <input data-testid="checkout-address" name="address" value={form.address} onChange={handleChange} placeholder="Street Address *" className={inputCls} />
              <div className="grid grid-cols-3 gap-3">
                <input data-testid="checkout-city" name="city" value={form.city} onChange={handleChange} placeholder="City *" className={inputCls} />
                <input data-testid="checkout-state" name="state" value={form.state} onChange={handleChange} placeholder="State" className={inputCls} />
                <input data-testid="checkout-zip" name="zip" value={form.zip} onChange={handleChange} placeholder="ZIP" className={inputCls} />
              </div>
              <textarea data-testid="checkout-notes" name="notes" value={form.notes} onChange={handleChange} rows={2} placeholder="Order notes (optional)" className={`${inputCls} resize-none`} />

              <div>
                <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-[#001B2A]">
                  <Lock className="h-4 w-4" /> Card Details
                </div>
                <div id="square-card-container" data-testid="square-card-container" />
                {!cardReady && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading secure payment form...
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  🔒 Payments securely processed by <strong>Square</strong>. All bow ties feature adjustable straps (fits neck sizes 11"–20").
                </p>
              </div>

              <button
                type="submit"
                data-testid="place-order-button"
                disabled={submitting || !cardReady}
                className="w-full flex items-center justify-center gap-2 bg-[#7B1E2B] text-white py-3.5 rounded-md font-semibold hover:bg-[#001B2A] transition-colors disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />}
                {submitting ? 'Processing Payment...' : `PAY $${total.toFixed(2)}`}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutForm;
